import { Controller } from "@hotwired/stimulus"
import { startLogin, resumeLogin, FlowAbandonedError } from "@zoreal/oauth2-js"

// "Continue with ZOREAL" on the sign-in pages.
//
// The SDK does the pairing: on a computer it draws the QR modal and holds the
// tapped button busy until the login ends; on a phone the tap itself navigates
// to the provider, which opens the ZOREAL ID app, and after the approval the
// app reopens this page, where connect() finishes the same login. Either way
// the outcome is a code, the PKCE verifier and the nonce, posted to the session
// endpoint, which answers with where to go next.
export default class extends Controller {
  static targets = ["button", "error"]
  static values = {
    clientId: String,
    issuer: String,
    url: String,
    scope: { type: String, default: "openid email profile.name" },
  }

  connect() {
    const returned = resumeLogin({
      clientId: this.clientIdValue,
      issuer: this.issuerValue || undefined,
      control: this.buttonTarget,
    })
    if (returned) this.finish(returned.promise)
  }

  // On the click, from the click handler itself: nothing may be awaited before
  // startLogin runs, or a phone loses the navigation that opens the app.
  login(event) {
    event.preventDefault()
    this.hideError()
    const handle = startLogin({
      flow: "auth-code",
      clientId: this.clientIdValue,
      issuer: this.issuerValue || undefined,
      scope: this.scopeValue,
      control: event.currentTarget,
    })
    this.finish(handle.promise)
  }

  async finish(promise) {
    let result
    try {
      result = await promise
    } catch (error) {
      // Closing the dialog, declining on the phone or letting the request
      // expire is not an error to show: the button is simply ready again.
      if (error instanceof FlowAbandonedError || error?.name === "AbortError") return
      this.showError(error?.description || error?.message || this.failureText)
      return
    }

    const button = this.buttonTarget
    button.disabled = true
    try {
      const activity = (await window.storeOAuthFingerprint?.()) || {}
      const response = await fetch(this.urlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken,
        },
        body: JSON.stringify({
          code: result.code,
          code_verifier: result.code_verifier,
          nonce: result.nonce,
          login_activity: { fingerprint: activity.fingerprint, timezone: activity.timezone },
        }),
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok || !data.redirect_to) {
        this.showError(data.error || this.failureText)
        button.disabled = false
        return
      }
      window.location.assign(data.redirect_to)
    } catch (error) {
      this.showError(error?.message || this.failureText)
      button.disabled = false
    }
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  get failureText() {
    return this.errorTarget.dataset.failure || "Sign in with ZOREAL did not go through."
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("d-none")
  }

  hideError() {
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("d-none")
  }
}
